import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Info } from 'lucide-react';
import apiService from '../../../services/apiService';
import StandardDatePicker from '../../../components/DatePicker/DatePicker';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
  onError?: (message: string) => void;
  editData?: any;
};

const quillModules = {
  toolbar: [
    [{ size: [] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

const extractArray = (res: any) => (Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.data) ? res.data.data : []);

const TemporaryOperatingInstructionModal: React.FC<Props> = ({ onClose, onSuccess, onError, editData }) => {
  const [title, setTitle] = useState('');
  const [siteId, setSiteId] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [unitStationId, setUnitStationId] = useState('');
  const [systemId, setSystemId] = useState('');
  const [docTypeId, setDocTypeId] = useState('');
  const [revId, setRevId] = useState('');
  const [hbsId, setHbsId] = useState('');
  const [reason, setReason] = useState('');
  const [temporaryInstruction, setTemporaryInstruction] = useState('');

  const [errors, setErrors] = useState<any>({});
  const validFromRef = useRef<HTMLInputElement>(null);
  const validToRef = useRef<HTMLInputElement>(null);

  const titleRef = useRef<HTMLInputElement>(null);
  const siteRef = useRef<HTMLSelectElement>(null);
  const unitStationRef = useRef<HTMLSelectElement>(null);
  const systemRef = useRef<HTMLSelectElement>(null);
  const docTypeRef = useRef<HTMLSelectElement>(null);
  const revRef = useRef<HTMLSelectElement>(null);
  const hbsRef = useRef<HTMLSelectElement>(null);
  const reasonRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState(false);

  const [sites, setSites] = useState<any[]>([]);
  const [unitStations, setUnitStations] = useState<any[]>([]);
  const [systems, setSystems] = useState<any[]>([]);
  const [docTypes, setDocTypes] = useState<any[]>([]);
  const [revs, setRevs] = useState<any[]>([]);
  const [hbsList, setHbsList] = useState<any[]>([]);
  const today = new Date().toISOString().split('T')[0];

  //  Helper function to find ID by matching text
  const findIdByText = (array: any[], textValue: string, fields: string[]) => {
    if (!textValue || !array.length) return '';
    const item = array.find((el) => fields.some((field) => el[field]?.toLowerCase() === textValue.toLowerCase()));
    return item ? String(item.id) : '';
  };

  // Extract plain text from ReactQuill HTML
  const getPlainText = (html: string) => {
    const temp = document.createElement('div');
    temp.innerHTML = html || '';
    return temp.textContent?.trim() || '';
  };

  // Lock background page scroll while this modal is mounted
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Fetch Dropdowns
  useEffect(() => {
    Promise.all([
      apiService.get('/sites'),
      apiService.get('/temporary-operating-instructions/dropdowns/unit-stations'),
      apiService.get('/temporary-operating-instructions/dropdowns/systems'),
      apiService.get('/temporary-operating-instructions/dropdowns/doc-types'),
      apiService.get('/temporary-operating-instructions/dropdowns/revs'),
      apiService.get('/temporary-operating-instructions/dropdowns/hbs'),
    ])
      .then(([sitesRes, unitRes, sysRes, docRes, revRes, hbsRes]) => {
        setSites(extractArray(sitesRes));
        setUnitStations(extractArray(unitRes));
        setSystems(extractArray(sysRes));
        setDocTypes(extractArray(docRes));
        setRevs(extractArray(revRes));
        setHbsList(extractArray(hbsRes));
      })
      .catch((err) => {
        console.error('Failed to load dropdowns:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load dropdown data';
        onError?.(errorMessage);
        onClose();
      });
  }, []);

  //  PREFILL DATA - runs AFTER dropdowns are loaded
  useEffect(() => {
    if (!editData || !sites.length || !unitStations.length) return;

    setTitle(editData.title || '');
    setValidFrom(editData.valid_from || '');
    setValidTo(editData.valid_to || '');
    setReason(editData.reason_for_temporary_instruction || '');
    setTemporaryInstruction(editData.temporary_instruction || '');

    // Map text values to IDs
    setSiteId(findIdByText(sites, editData.site, ['name', 'title']));
    setUnitStationId(findIdByText(unitStations, editData.unit_station, ['name', 'title', 'code']));
    setSystemId(findIdByText(systems, editData.system, ['name', 'title', 'code']));
    setDocTypeId(findIdByText(docTypes, editData.doc_type, ['type', 'title', 'code']));
    setRevId(findIdByText(revs, editData.rev, ['rev', 'title', 'code']));
    setHbsId(findIdByText(hbsList, editData.hbs, ['hbs', 'title', 'code']));
  }, [editData, sites, unitStations, systems, docTypes, revs, hbsList]);

  const focusFirstError = (errors: any) => {
    const fieldRefs: any = {
      title: titleRef,
      siteId: siteRef,
      validFrom: validFromRef,
      validTo: validToRef,
      unitStationId: unitStationRef,
      systemId: systemRef,
      docTypeId: docTypeRef,
      revId: revRef,
      hbsId: hbsRef,
      reason: reasonRef,
    };

    const firstErrorField = Object.keys(errors)[0];

    if (firstErrorField && fieldRefs[firstErrorField]?.current) {
      fieldRefs[firstErrorField].current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      setTimeout(() => {
        fieldRefs[firstErrorField].current.focus?.();
      }, 300);
    }
  };

  const validate = () => {
    const newErrors: any = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.trim().length > 100) {
      newErrors.title = 'Maximum 100 characters allowed';
    }
    if (!siteId) newErrors.siteId = 'Site is required';
    // Valid From validation
    if (!validFrom) {
      newErrors.validFrom = 'Valid From is required';
    } else {
      const selectedFromYear = new Date(validFrom).getFullYear();

      if (selectedFromYear < 1000 || selectedFromYear > 9999) {
        newErrors.validFrom = 'Please enter a valid year';
      }

      if (validFrom < today) {
        newErrors.validFrom = 'Past dates are not allowed for Valid From';
      }
    }

    // Valid To validation
    if (!validTo) {
      newErrors.validTo = 'Valid To is required';
    } else {
      const selectedToYear = new Date(validTo).getFullYear();

      if (selectedToYear < 1000 || selectedToYear > 9999) {
        newErrors.validTo = 'Please enter a valid year';
      }

      if (validTo < today) {
        newErrors.validTo = 'Past dates are not allowed for Valid To';
      }
    }

    // Valid To should not be before Valid From
    if (validFrom && validTo && validTo < validFrom) {
      newErrors.validTo = 'Valid To date cannot be earlier than Valid From date';
    }
    if (!unitStationId) newErrors.unitStationId = 'Unit-Station is required';
    if (!systemId) newErrors.systemId = 'System is required';
    if (!docTypeId) newErrors.docTypeId = 'Doc Type is required';
    if (!revId) newErrors.revId = 'Revision is required';
    if (!hbsId) newErrors.hbsId = 'HBS is required';
    //if (!reason || reason === '<p><br></p>') newErrors.reason = 'Reason is required';
    //if (!temporaryInstruction || temporaryInstruction === '<p><br></p>')
    //newErrors.temporaryInstruction = 'Temporary Instruction is required';
    const reasonText = getPlainText(reason);

    if (!reasonText) {
      newErrors.reason = 'This field is required';
    } else if (reasonText.length > 500) {
      newErrors.reason = 'Maximum 500 characters allowed';
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      focusFirstError(newErrors);
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      title,
      site_id: Number(siteId),
      valid_from: validFrom,
      valid_to: validTo,
      unit_station_id: Number(unitStationId),
      system_id: Number(systemId),
      doc_type_id: Number(docTypeId),
      rev_id: Number(revId),
      hbs_id: Number(hbsId),
      reason_for_temporary_instruction: reason,
      temporary_instruction: temporaryInstruction,
    };

    try {
      setIsLoading(true);
      if (editData?.id) {
        await apiService.put(`/temporary-operating-instructions/${editData.id}`, payload);
      } else {
        await apiService.post('/temporary-operating-instructions', payload);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Save failed:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to save Temporary Operating Instruction';
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const ErrorText = ({ msg }: { msg?: string }) => (msg ? <p className="text-red-500 text-xs mt-1">{msg}</p> : null);

  const Label = ({ text }: { text: string }) => (
    <div className="flex items-center gap-1 mb-1">
      <span className="text-sm font-medium text-gray-700">{text}</span>
      <Info size={14} className="text-gray-400" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white w-[1000px] rounded-xl shadow-lg max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center px-8 py-5 flex-shrink-0">
          <h2 className="text-lg font-semibold">
            {editData ? 'Edit Temporary Operating Instruction' : 'Add Temporary Operating Instruction'}
          </h2>
          <button onClick={onClose} className="text-xl text-gray-400">
            ✕
          </button>
        </div>

        <div className="px-8 py-6 overflow-y-auto flex-1 grid grid-cols-2 gap-6">
          <div>
            <label className="flex items-center text-sm font-medium mb-2">
              Title
              <Info size={14} className="ml-1 text-gray-400" />
              <span className="text-red-500 ml-1">*</span>
            </label>

            <input
              ref={titleRef}
              placeholder="Enter Title"
              value={title}
              maxLength={100}
              onChange={(e) => {
                const value = e.target.value;

                if (value.length <= 100) {
                  setTitle(value);

                  setErrors((prev: any) => ({
                    ...prev,
                    title: '',
                  }));
                } else {
                  setErrors((prev: any) => ({
                    ...prev,
                    title: 'Maximum 100 characters allowed',
                  }));
                }
              }}
              className="w-full border rounded-md px-3 py-2 bg-gray-100
                text-gray-700
                placeholder:text-gray-400
                outline-none focus:outline-none focus:ring-0 focus:border-gray-300"
            />

            {errors.title && <div className="text-red-500 text-xs mt-1">{errors.title}</div>}
            <div className="flex justify-end mt-1">
              <span className={`text-xs ${title.length >= 100 ? 'text-red-500' : 'text-gray-500'}`}>{title.length}/100</span>
            </div>
          </div>

          <div>
            <label className="flex items-center text-sm font-medium mb-2">
              Site
              <Info size={14} className="ml-1 text-gray-400" />
              <span className="text-red-500 ml-1">*</span>
            </label>

            <select
              ref={siteRef}
              className={`w-full border rounded-md px-3 py-2 bg-gray-100
              ${!siteId ? 'text-gray-400' : 'text-gray-700'}
              outline-none focus:outline-none focus:ring-0 focus:border-gray-300`}
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
            >
              <option value="" disabled hidden>
                Select Site
              </option>
              {sites.map((s) => (
                <option key={s.id} value={s.id} className="text-gray-700">
                  {s.name || s.title}
                </option>
              ))}
            </select>

            {errors.siteId && <div className="text-red-500 text-xs mt-1">{errors.siteId}</div>}
          </div>

          <div>
            <div className="flex items-center gap-1 mb-1">
              <span className="text-sm font-medium text-gray-700">Valid From</span>
              <Info size={14} className="text-gray-400" />
              <span className="text-red-500">*</span>
            </div>
            <StandardDatePicker
              inputRef={validFromRef}
              value={validFrom}
              min={today}
              error={Boolean(errors.validFrom)}
              onChange={(selectedDate) => {
                setValidFrom(selectedDate);
                setErrors((prev: any) => ({ ...prev, validFrom: '' }));
              }}
            />
            <ErrorText msg={errors.validFrom} />
          </div>

          <div>
            <div className="flex items-center gap-1 mb-1">
              <span className="text-sm font-medium text-gray-700">Valid To</span>
              <Info size={14} className="text-gray-400" />
              <span className="text-red-500">*</span>
            </div>
            <StandardDatePicker
              inputRef={validToRef}
              value={validTo}
              min={validFrom || today}
              error={Boolean(errors.validTo)}
              onChange={(selectedDate) => {
                setValidTo(selectedDate);
                setErrors((prev: any) => ({ ...prev, validTo: '' }));
              }}
            />
            <ErrorText msg={errors.validTo} />
          </div>

          <div>
            <label className="flex items-center text-sm font-medium mb-2">
              Unit-Station
              <Info size={14} className="ml-1 text-gray-400" />
              <span className="text-red-500 ml-1">*</span>
            </label>

            <select
              ref={unitStationRef}
              className={`w-full border rounded-md px-3 py-2 bg-gray-100
              ${!unitStationId ? 'text-gray-400' : 'text-gray-700'}
              outline-none focus:outline-none focus:ring-0 focus:border-gray-300`}
              value={unitStationId}
              onChange={(e) => setUnitStationId(e.target.value)}
            >
              <option value="" disabled hidden>
                Select Unit-Station
              </option>
              {unitStations.map((u) => (
                <option key={u.id} value={u.id} className="text-gray-700">
                  {u.name || u.title || u.code}
                </option>
              ))}
            </select>

            {errors.unitStationId && <div className="text-red-500 text-xs mt-1">{errors.unitStationId}</div>}
          </div>
          <div>
            <label className="flex items-center text-sm font-medium mb-2">
              System
              <Info size={14} className="ml-1 text-gray-400" />
              <span className="text-red-500 ml-1">*</span>
            </label>

            <select
              ref={systemRef}
              className={`w-full border rounded-md px-3 py-2 bg-gray-100
              ${!systemId ? 'text-gray-400' : 'text-gray-700'}
              outline-none focus:outline-none focus:ring-0 focus:border-gray-300`}
              value={systemId}
              onChange={(e) => setSystemId(e.target.value)}
            >
              <option value="" disabled hidden>
                Select System
              </option>
              {systems.map((s) => (
                <option key={s.id} value={s.id} className="text-gray-700">
                  {s.name || s.title || s.code}
                </option>
              ))}
            </select>

            {errors.systemId && <div className="text-red-500 text-xs mt-1">{errors.systemId}</div>}
          </div>

          <div>
            <label className="flex items-center text-sm font-medium mb-2">
              Doc Type
              <Info size={14} className="ml-1 text-gray-400" />
              <span className="text-red-500 ml-1">*</span>
            </label>

            <select
              ref={docTypeRef}
              className={`w-full border rounded-md px-3 py-2 bg-gray-100
              ${!docTypeId ? 'text-gray-400' : 'text-gray-700'}
              outline-none focus:outline-none focus:ring-0 focus:border-gray-300`}
              value={docTypeId}
              onChange={(e) => setDocTypeId(e.target.value)}
            >
              <option value="" disabled hidden>
                Select Document Type
              </option>
              {docTypes.map((d) => (
                <option key={d.id} value={d.id} className="text-gray-700">
                  {d.type || d.title || d.code}
                </option>
              ))}
            </select>

            {errors.docTypeId && <div className="text-red-500 text-xs mt-1">{errors.docTypeId}</div>}
          </div>

          <div>
            <label className="flex items-center text-sm font-medium mb-2">
              Rev
              <Info size={14} className="ml-1 text-gray-400" />
              <span className="text-red-500 ml-1">*</span>
            </label>

            <select
              ref={revRef}
              className={`w-full border rounded-md px-3 py-2 bg-gray-100
              ${!revId ? 'text-gray-400' : 'text-gray-700'}
              outline-none focus:outline-none focus:ring-0 focus:border-gray-300`}
              value={revId}
              onChange={(e) => setRevId(e.target.value)}
            >
              <option value="" disabled hidden>
                Select Rev
              </option>
              {revs.map((r) => (
                <option key={r.id} value={r.id} className="text-gray-700">
                  {r.rev || r.title || r.code}
                </option>
              ))}
            </select>

            {errors.revId && <div className="text-red-500 text-xs mt-1">{errors.revId}</div>}
          </div>

          <div>
            <label className="flex items-center text-sm font-medium mb-2">
              HBS
              <Info size={14} className="ml-1 text-gray-400" />
              <span className="text-red-500 ml-1">*</span>
            </label>

            <select
              ref={hbsRef}
              className={`w-full border rounded-md px-3 py-2 bg-gray-100
              ${!hbsId ? 'text-gray-400' : 'text-gray-700'}
              outline-none focus:outline-none focus:ring-0 focus:border-gray-300`}
              value={hbsId}
              onChange={(e) => setHbsId(e.target.value)}
            >
              <option value="" disabled hidden>
                Select HBS
              </option>
              {hbsList.map((h) => (
                <option key={h.id} value={h.id} className="text-gray-700">
                  {h.hbs || h.title || h.code}
                </option>
              ))}
            </select>

            {errors.hbsId && <div className="text-red-500 text-xs mt-1">{errors.hbsId}</div>}
          </div>

          <div className="col-span-2">
            <div className="flex items-center gap-1 mb-1">
              <span className="text-sm font-medium text-gray-700">Reason for Temporary Operating Instruction</span>
              <Info size={14} className="text-gray-400" />
              <span className="text-red-500">*</span>
            </div>

            <div
              ref={reasonRef}
              tabIndex={-1}
              className={`bg-gray-100 border rounded ${errors.reason ? 'border-red-500' : 'border-gray-300'}`}
            >
              <ReactQuill
                value={reason}
                onChange={(content) => {
                  const temp = document.createElement('div');
                  temp.innerHTML = content;

                  const plainText = temp.textContent || '';

                  // Allow only up to 500 characters
                  if (plainText.length <= 500) {
                    setReason(content);

                    setErrors((prev: any) => ({
                      ...prev,
                      reason: '',
                    }));
                  } else {
                    setErrors((prev: any) => ({
                      ...prev,
                      reason: 'Maximum 500 characters allowed',
                    }));
                  }
                }}
                modules={quillModules}
                className="bg-gray-100"
                placeholder="Add description here"
              />
            </div>
            <div className="flex justify-end mt-1">
              <span className={`text-xs ${getPlainText(reason).length > 500 ? 'text-red-500' : 'text-gray-500'}`}>
                {getPlainText(reason).length}/500
              </span>
            </div>

            <ErrorText msg={errors.reason} />
          </div>

          {/* <div className="col-span-2">
            <Label text="Temporary Instruction" />

            <div className="bg-gray-100 border border-gray-300 rounded">
              <ReactQuill
                value={temporaryInstruction}
                onChange={setTemporaryInstruction}
                modules={quillModules}
                className="bg-gray-100"
                placeholder="Add description here"
              />
            </div>

            <ErrorText msg={errors.temporaryInstruction} />
          </div> */}
        </div>

        <div className="flex justify-end gap-3 px-8 py-4 border-t flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 border rounded-md">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-5 py-2 bg-red-600 text-white rounded-md flex items-center gap-2 disabled:opacity-70"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {editData ? 'Updating...' : 'Adding...'}
              </>
            ) : editData ? (
              'Update'
            ) : (
              'Add'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemporaryOperatingInstructionModal;
